import { t as msg } from "../lib/i18n";
import { useState } from "react";
import { ArrowDownToLine, Trash2 } from "lucide-react";
import { draftStore } from "../lib/drafts";
import { api, ApiError } from "../lib/api";
export function DataTools({
  scope,
  ownerId,
  onDeleted,
  signIn,
}: {
  scope: "account" | "guest";
  ownerId: string;
  onDeleted: (localCleanupFailed: boolean) => void;
  signIn?: () => void;
}) {
  const [reauth,setReauth]=useState(false);
  const [confirmation, setConfirmation] = useState(""),
    [busy, setBusy] = useState(false),
    [exporting, setExporting] = useState(false),
    [error, setError] = useState("");
  async function erase(e: React.FormEvent) {
    e.preventDefault();
    if (busy || exporting) return;
    setBusy(true);
    setError("");
    try {
      await api("/api/privacy/delete", "POST", {
        confirmation,
        ownerId,
        scope,
      });
      let localCleanupFailed = false;
      try { await draftStore(scope === "account" ? ownerId : null).clear(); } catch { localCleanupFailed = true; }
      onDeleted(localCleanupFailed);
    } catch (e) {
      if(e instanceof ApiError && e.code === "recent_sign_in_required") setReauth(true);
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function download() {
    if (busy || exporting) return;
    setExporting(true); setError("");
    try {
      const data = await api<unknown>("/api/privacy/export");
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const link = document.createElement("a"); link.href = url; link.download = "apromisecard-data.json"; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError((e as Error).message); }
    finally { setExporting(false); }
  }
  return (
    <section className="profile-section" id="data" aria-labelledby={"data-title"}>
      <p className="eyebrow">{msg("YOURS TO MANAGE")}</p>
      <h2 id="data-title">{msg("Your data.")}</h2>
      <p>
        {msg("Download your cards, requests, thanks and contact messages. Your file excludes credentials and other people’s private submissions.")}</p>
      <button className="button secondary" disabled={busy || exporting} onClick={() => void download()}><ArrowDownToLine size={17} /> {exporting ? msg("Please wait…") : msg("Download my data")}</button>
      {error && <p role="alert" className="error">{error}</p>}
      {reauth&&signIn&&<button className="button secondary" onClick={signIn}>{msg("Sign in again")}</button>}
      <details className="delete-disclosure">
        <summary>
          <Trash2 size={16} /> {scope === "account" ? msg("Delete account") : msg("Delete guest cards")}
        </summary>
        <p>
          {msg("This is permanent.")}{" "}
          {scope === "account"
            ? msg("Your sign-in access, sent cards will be removed. Sign in within the last 15 minutes before deleting.")
            : msg("This browser’s guest cards access and its requests and thanks will be removed. Other people’s original promises remain.")}
        </p>
        {scope === "account" && <p>{msg("Download your information first. Deleting this account also removes its drafts from this browser.")}</p>}
        <form onSubmit={erase}>
          <label>
            {msg("Type DELETE to confirm")}<input
              disabled={busy || exporting}
              autoComplete="off"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </label>
          <button
            className="button danger"
            disabled={busy || exporting || confirmation !== "DELETE"}
          >
            {busy
              ? msg("Deleting…")
              : scope === "account" ? msg("Permanently delete my account") : msg("Permanently delete my guest cards")}
          </button>
        </form>
      </details>
      <p className="profile-help">
        <a href="/contact?topic=privacy">{msg("Get help with your data")}</a> ·{" "}
        <a href="/privacy">{msg("Privacy notice")}</a>
      </p>
    </section>
  );
}
export function DeletedData({ localCleanupFailed = false }: { localCleanupFailed?: boolean }) {
  return (
    <div className="empty" role="status">
      <h2>{msg("Your deletion is confirmed.")}</h2>
      <p>
        {msg("Your access has been removed and your submitted text erased from the live database. Stored images are queued for cleanup. Copies held by others and minimal technical or email opt-out records may remain as described in our privacy notice.")}</p>
      {localCleanupFailed && <p role="alert" className="error">{msg("Your data was deleted, but this device could not remove its local drafts. Clear this site’s browser data to remove them.")}</p>}
      <a className="button" href="/">
        {msg("Back to home")}</a>
    </div>
  );
}
